import type {
  WorkflowContext,
  WorkflowNavigationItem,
  WorkflowStepOptions,
  WorkflowOptions,
  WorkflowResponse,
  WorkflowStatusResponse,
  WorkflowStepStatus,
  WorkflowStatus,
  AssetTypes,
  ConditionalMatcher,
  WorkflowType,
  Asset,
  AssetState,
} from './types';
import { WorkflowStep } from './WorkflowStep';
import type Ajv from 'ajv';
import { workflowValidationSchema } from './workflowValidationSchema';

export class Workflow {
  public readonly name: string;
  public readonly shipiumOnly?: boolean;
  public readonly workflowActionIcon?: string;
  public readonly workflowDescription?: string;
  public readonly workflowHelp?: string;
  private readonly _workflowKey: string;
  public readonly workflowType: WorkflowType;
  public readonly workflowAssets: AssetTypes[];
  public readonly workflowGroupKey: string;
  public workflowStepMap: Map<string, WorkflowStep>;
  public workflowSteps: WorkflowStep[];
  public readonly hideIfDisabled?: boolean;
  public readonly workflowConditionalMatcher: ConditionalMatcher[];
  public workflowConditionalValidator: Ajv.ValidateFunction;
  public workflowStateTransformFunction: (wfs: any, asset?: Asset) => any;

  constructor(workflowGroupKey: string, options: WorkflowOptions, assetState: AssetState, isShipiumAdmin?: boolean) {
    this.name = options.workflowName;
    this.workflowActionIcon = options.workflowActionIcon;
    this._workflowKey = options.workflowKey;
    this.workflowType = options.workflowType;
    this.workflowGroupKey = workflowGroupKey;
    this.workflowDescription = options.workflowDescription;
    this.workflowAssets = options.workflowAssets || [];
    this.workflowSteps = Workflow.constructSteps(
      this.workflowKey,
      options.workflowStepOptions,
      assetState,
      isShipiumAdmin,
    );
    this.workflowStepMap = Workflow.constructStepMap(this.workflowSteps);
    this.hideIfDisabled = options.hideIfDisabled;
    this.workflowStateTransformFunction = options.workflowStateTransformFunction
      ? options.workflowStateTransformFunction
      : (wfs: any) => wfs;
    this.workflowConditionalMatcher = options.workflowConditionalMatcher || [];
    this.workflowConditionalValidator = workflowValidationSchema.compile(
      this.workflowConditionalMatcher,
      this.workflowKey,
      assetState,
    );
  }

  public get workflowKey(): string {
    return this._workflowKey;
  }

  public static constructStepMap(workflowSteps: WorkflowStep[]): Map<string, WorkflowStep> {
    return workflowSteps.reduce((prev, curr) => {
      return prev.set(curr.workflowStepKey, curr);
    }, new Map<string, WorkflowStep>());
  }

  public static constructSteps(
    workflowKey: string,
    workflowStepOptions: WorkflowStepOptions[],
    assetState: AssetState,
    isShipiumAdmin?: boolean,
    asset?: Asset,
  ): WorkflowStep[] {
    return workflowStepOptions
      .filter((option) => !option.shipiumOnly || isShipiumAdmin)
      .map((option, index) => {
        option.workflowStepIndex = option.workflowStepIndex || index + 1;
        return option;
      })
      .sort((a, b) =>
        (a.workflowStepIndex || 0) < (b.workflowStepIndex || 0)
          ? -1
          : (a.workflowStepIndex || 0) > (b.workflowStepIndex || 0)
            ? 1
            : 0,
      )
      .map((options) => new WorkflowStep(workflowKey, options, assetState));
  }

  public static getDisplayNameForWorkflowType(workflowType: WorkflowType) {
    const workflowTypeToDescriptionMap: Record<WorkflowType, string> = {
      addAssetWorkflow: 'Add Asset Workflow',
      editAssetWorkflow: 'Edit Asset Workflow',
      configurationWorkflow: 'Configuration Workflow',
    };
    return workflowTypeToDescriptionMap[workflowType] || `Unknown WorkflowType: ${workflowType}`;
  }

  // Walks through all workflow steps in reverse order, building out a navigation item array
  // for steps which pass validation.
  public buildNavigation(workflowContext: WorkflowContext) {
    let allComplete = true;
    return this.workflowSteps.reduce<WorkflowNavigationItem[]>((prev, curr) => {
      const isSummary = curr.isSummaryStep();
      if (curr.excludeFromNavigation) {
        return prev;
      }
      if (curr.validate(workflowContext)) {
        if (!isSummary || (isSummary && allComplete)) {
          allComplete = curr.isComplete(workflowContext) && allComplete;
          prev.push({
            workflowStepKey: curr.workflowStepKey,
            workflowStepNavigationName: curr.workflowStepNavigationName,
            workflowStepComplete: curr.isComplete(workflowContext),
          });
        }
      }
      return prev;
    }, []);
  }

  public buildNext(workflowContext: WorkflowContext) {
    for (const step of this.workflowSteps) {
      // skip over complete steps
      if (step.isComplete(workflowContext)) {
        continue;
        // incomplete steps
      } else if (step.validate(workflowContext)) {
        return step.build(workflowContext);
      } else {
        continue;
      }
    }
    // if we fall through, build the last step
    return this.workflowSteps[this.workflowSteps.length - 1].build(workflowContext);
  }

  public static deriveWorkflowStatusFromWorkflowResponse(workflowResponse: WorkflowResponse): WorkflowStatus {
    const completed = workflowResponse.workflowComponent?.workflowComponentType === 'workflowSummary';

    if (completed) {
      return 'completed';
    }
    const notStarted = workflowResponse.workflowNavigation?.reduce((prev, curr) => {
      return !curr.workflowStepComplete && prev;
    }, true);

    if (notStarted) {
      return 'notStarted';
    }

    return 'inProgress';
  }

  public validate(context: WorkflowContext) {
    return this.workflowConditionalValidator(context);
  }

  public status(workflowContext: WorkflowContext): WorkflowStatusResponse {
    const workflowResponse = this.handle(workflowContext);
    const workflowDisabled = !this.validate(workflowContext);
    const workflowStatus = workflowDisabled
      ? 'disabled'
      : Workflow.deriveWorkflowStatusFromWorkflowResponse(workflowResponse);
    const workflowStepStatuses = this.workflowSteps.reduce<WorkflowStepStatus[]>((prev, curr) => {
      if (curr.excludeFromNavigation) {
        return prev;
      }
      const isValid = !!curr.validate(workflowContext);
      const isComplete = curr.isComplete(workflowContext);
      const isSummary = curr.isSummaryStep();
      if (workflowDisabled) {
        return prev;
      }
      if ((workflowStatus === 'completed' && isValid) || (workflowStatus !== 'completed' && !isSummary)) {
        const status: WorkflowStepStatus = {
          workflowStepValid: isValid,
          workflowStepSkipped: !!curr.skipped,
          workflowStepComplete: isComplete,
          workflowStepKey: curr.workflowStepKey,
          workflowStepNavigationName: curr.workflowStepNavigationName,
          workflowStepPrompt: (curr.workflowComponent as any).prompt,
        };

        if (status.workflowStepValid) {
          const workflowState = ((workflowContext.workflowState as any) || {})[this.workflowKey] || {};
          const workflowStepState = workflowState[curr.workflowStepKey || 'NA'];
          status.configuredValue = workflowStepState;
        }
        prev.push(status);
      }
      return prev;
    }, []);

    return {
      workflowStatus,
      workflowHidden: workflowDisabled,
      workflowStepStatus: workflowStepStatuses,
    };
  }

  public handle(workflowContext: WorkflowContext): WorkflowResponse {
    const { workflowKey, workflowStepKey, workflowFromStepKey } = workflowContext;
    try {
      if (workflowKey !== this.workflowKey) {
        throw new Error('Workflow key mismatch.');
      }
      if (workflowStepKey) {
        const step = this.workflowStepMap.get(workflowStepKey);

        if (!step) {
          throw new Error(`Workflow step key ${workflowStepKey} not found.`);
        }
        return {
          optional: step.optional,
          workflowKey,
          workflowFromStepKey,
          workflowNavigation: this.buildNavigation(workflowContext),
          ...step.build(workflowContext),
        };
      } else {
        return {
          workflowKey,
          workflowFromStepKey,
          workflowNavigation: this.buildNavigation(workflowContext),
          ...this.buildNext(workflowContext),
        };
      }
    } catch (workflowError) {
      if (process.env.NODE_ENV !== 'test') {
        console.error(workflowError);
      }
      return {
        workflowKey,
        workflowContext,
        workflowError,
        workflowFromStepKey,
        workflowComponent: {
          workflowComponentType: 'workflowError',
          title: 'Workflow Error',
          description: workflowError.message,
          help: '',
        },
        refreshAssetsOnStepCompletion: false,
        workflowStepKey: 'WORKFLOW_ERROR',
      };
    }
  }
}
